import {setSessions} from "@/redux/slices/commonSlices/viewTabsSlice";
import {
  clearSummarizationState,
  getSummarizingFeedback,
  setError,
  setFromDate,
  setSelectedValues,
  setToDate,
  viewSummarizingFeedback,
} from "@/redux/slices/mlSlices/summarizationSlice";

import {useEffect, useRef, useState} from "react";

import {useViewTabs} from "@hooks/ViewTabs";
import {getScenarios} from "@redux/slices/productSlices/optionsSlice";
import {getProducts} from "@redux/slices/scenarioSlices/optionsSlice";
import {getAllScenarios} from "@redux/slices/scenarioSlices/tableSlice";
import {ControlButton} from "@ui/buttons/ControlButton";
import {RootButton} from "@ui/buttons/RootButton";
import {SwitchButton} from "@ui/buttons/SwitchButton";
import {DropRangeCalendar} from "@ui/general/DropRangeCalendar";
import {Spinner} from "@ui/general/Spinner";
import {WarningData} from "@ui/general/WarningData";
import EditSelect from "@ui/inputs/EditSelect";
import {H3} from "@ui/titles/H3";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import {FeedbackHistory} from "./FeedbackHistory";
import {FeedbackResult} from "./FeedbackResult";
import styles from "./styles.module.scss";

export const Feedback = () => {
  const minDate = moment().subtract(1, "years").toDate();

  const [showHistory, setShowHistory] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [cameFromHistory, setCameFromHistory] = useState(false);

  const {serverStatus, error, fromDate, toDate, selectedValues} = useSelector(
    (state) => state.ml.summarization,
  );
  const {sessions} = useSelector((state) => state.common.viewTabs);
  const {products} = useSelector((state) => state.scenario.options);
  const {scenarios, certifications} = useSelector(
    (state) => state.product.options.scenarios,
  );

  const [currentUserId, setCurrentUserId] = useState(null);

  const userRole = useSelector((state) => state.common.candidate.role);
  const userId = useSelector((state) => state.common.candidate.id);
  const targetUserRole = useSelector(
    (state) => state?.user?.view?.current?.role,
  );
  const targetUserId = useSelector((state) => state?.user?.view?.current?.id);

  const dispatch = useDispatch();
  const selectRef = useRef(null);

  const {t} = useTranslation();
  const {SESSIONS_VIEW_TABS} = useViewTabs();

  const handleClickHistory = () => {
    setShowResult(false);
    setShowHistory(!showHistory);
  };

  const handleClickTab = () => {
    setShowHistory(false);
    setShowResult(false);
  };

  const handleTabChange = (value) => {
    dispatch(setSessions(value));
    handleClickTab();
  };

  const handleClickGenerate = async () => {
    if (fromDate === null || toDate === null || selectedValues.length === 0) {
      dispatch(
        setError(
          t("server_error.feedback_generation_validation", {
            sessions:
              sessions === "product"
                ? t("table.product")
                : sessions === "scenario"
                  ? t("table.scenario")
                  : t("table.certification"),
          }),
        ),
      );

      return;
    }

    let resultAction;

    if (sessions === "certification") {
      resultAction = await dispatch(
        getSummarizingFeedback({
          source_type: "certification",
          certification_scenario_ids: selectedValues.map((item) => item.id),
          ...(currentUserId && {target_user_id: currentUserId}),
          start_date: moment(fromDate).startOf("day").toDate(),
          end_date: moment(toDate).startOf("day").add(1, "day").toDate(),
        }),
      );
    } else if (sessions === "scenario") {
      resultAction = await dispatch(
        getSummarizingFeedback({
          source_type: "scenario",
          scenario_ids: selectedValues.map((item) => item.id),
          ...(currentUserId && {target_user_id: currentUserId}),
          start_date: moment(fromDate).startOf("day").toDate(),
          end_date: moment(toDate).startOf("day").add(1, "day").toDate(),
        }),
      );
    } else if (sessions === "product") {
      resultAction = await dispatch(
        getSummarizingFeedback({
          source_type: "product",
          ...(selectedValues.length > 0 && {product_id: selectedValues[0].id}),
          ...(currentUserId && {target_user_id: currentUserId}),
          start_date: moment(fromDate).startOf("day").toDate(),
          end_date: moment(toDate).startOf("day").add(1, "day").toDate(),
        }),
      );
    }

    if (getSummarizingFeedback.fulfilled.match(resultAction)) {
      setShowResult(true);
      setCameFromHistory(false);
    } else {
      dispatch(setError(t("server_error.feedback_generation_empty")));
    }
  };

  const handleClickView = async (item) => {
    const resultAction = await dispatch(
      viewSummarizingFeedback({id: item?.id}),
    );

    if (viewSummarizingFeedback.fulfilled.match(resultAction)) {
      setShowHistory(false);
      setShowResult(true);
      setCameFromHistory(true);
    } else {
      dispatch(setError("Error"));
    }
  };

  const handleClickBackHistory = () => {
    setShowResult(false);
    if (cameFromHistory) {
      setShowHistory(true);
      setCameFromHistory(false);
    }
  };

  const handleClickBack = () => {
    setShowHistory(false);
    setShowResult(false);
    setCameFromHistory(false);
  };

  const handleSelectChange = (selectedOption) => {
    const newValues = selectedValues.some(
      (item) => item.id === selectedOption.id,
    )
      ? selectedValues.filter((item) => item.id !== selectedOption.id)
      : sessions === "scenario"
        ? [
            ...selectedValues,
            {id: selectedOption.id, name: selectedOption.name},
          ]
        : [{id: selectedOption.id, name: selectedOption.name}];

    dispatch(setSelectedValues(newValues));
  };

  useEffect(() => {
    if (userRole !== "Admin" && userId === targetUserId) {
      setCurrentUserId(null);
    } else {
      setCurrentUserId(targetUserId);
    }
  }, [userRole, userId, targetUserRole, targetUserId]);

  useEffect(() => {
    dispatch(clearSummarizationState());

    if (sessions === "product") {
      dispatch(getProducts());
    } else if (sessions === "scenario") {
      dispatch(getScenarios());
    } else if (sessions === "certification") {
      dispatch(getAllScenarios());
    }
  }, [sessions]);

  useEffect(() => {
    return () => {
      dispatch(clearSummarizationState());
    };
  }, []);

  return (
    <div className={styles.feedback}>
      {serverStatus === "loading" && (
        <Spinner
          width={80}
          height={80}
        />
      )}

      <div
        className={
          showHistory
            ? `${styles.feedbackHead} ${styles.feedbackHeadActive}`
            : `${styles.feedbackHead}`
        }
      >
        <H3>{t("page.profile.feedback.title")}</H3>

        <ControlButton
          iconId={"history"}
          color={"var(--blue)"}
          txt={t("button.history")}
          onClick={handleClickHistory}
        />

        <SwitchButton
          value={sessions}
          onChange={handleTabChange}
          switchList={SESSIONS_VIEW_TABS}
          onClickTab={(item) => handleClickTab(item)}
          width={630}
        />
      </div>

      {!showHistory && !showResult && (
        <div className={styles.feedbackSelect}>
          <div className={styles.feedbackSelectSimulation}>
            <H3>
              <span>
                {sessions === "certification"
                  ? t("input.select_certification_label")
                  : sessions === "scenario"
                    ? t("input.select_call_label")
                    : t("input.select_product_label")}
              </span>
            </H3>

            <div
              className={styles.feedbackSearchSimulation}
              ref={selectRef}
            >
              <EditSelect
                height={150}
                multiSelect={sessions === "scenario" ? true : false}
                activeOption={selectedValues.map((item) => item.name)}
                options={
                  sessions === "certification"
                    ? certifications
                    : sessions === "scenario"
                      ? scenarios
                      : products
                }
                onSelect={(selectedOption) =>
                  handleSelectChange(selectedOption)
                }
                searchPlaceholder={
                  sessions === "certification"
                    ? t("input.certification_search")
                    : sessions === "scenario"
                      ? t("input.scenario_search")
                      : t("input.product_search")
                }
              />
            </div>
          </div>

          <div className={styles.feedbackSelectDate}>
            <H3>
              <span>{t("input.select_date")}</span>
            </H3>

            <DropRangeCalendar
              minDate={minDate}
              fromDate={new Date(fromDate)}
              setFromDate={(date) => dispatch(setFromDate(date.toISOString()))}
              toDate={new Date(toDate)}
              setToDate={(date) => dispatch(setToDate(date.toISOString()))}
            />
          </div>

          <div className={styles.feedbackGenerateBtn}>
            <RootButton
              onClick={handleClickGenerate}
              txt={t("button.generate_feedback")}
              disabled={fromDate === null || toDate === null}
            />
          </div>
        </div>
      )}

      {error && (
        <WarningData
          type={"error"}
          desc={error}
        />
      )}

      {showHistory && (
        <FeedbackHistory
          onClickView={handleClickView}
          onClickBack={handleClickBack}
        />
      )}

      {showResult && <FeedbackResult onClickBack={handleClickBackHistory} />}
    </div>
  );
};
